import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { CognitoAuthService } from './cognito-auth.service';
import { GenerateContentLogParams } from '../models/log.model';

/**
 * Sends usage events to the logging API described in docs/send-log-procedure.md.
 * The endpoint URL is environment-specific (dev/stage/prod), see src/environments.
 */
@Injectable({
  providedIn: 'root',
})
export class LogService {
  private readonly sendLogsUrl = environment.logging.sendLogsUrl;

  constructor(
    private http: HttpClient,
    private cognitoAuthService: CognitoAuthService
  ) { }

  /**
   * Logs a `generate_content` event. Failures are swallowed so logging never
   * disrupts the calling feature's flow.
   */
  logGenerateContent(params: GenerateContentLogParams): Observable<any> {
    return this.cognitoAuthService.userData$.pipe(
      take(1),
      switchMap((userData) => {
        const log = {
          user_id: userData?.sub,
          date: new Date().toISOString(),
          event_name: (params.eventName !== undefined ? params.eventName : 'generate_content'),
          tool: params.tool,
          ...(params.model ? { model: params.model } : {}),
          ...(params.modelName ? { model_name: params.modelName } : {}),
          ...(params.imageCount !== undefined ? { image_count: params.imageCount } : {}),
          ...(params.contentType ? { content_type: params.contentType } : {}),
          ...(params.videoSize ? { video_size: params.videoSize } : {}),
          region: userData?.zoneinfo
        };
        
        return this.http.post(this.sendLogsUrl, { log });
      }),
      catchError((error) => {
        console.warn('Failed to send log event:', error);
        return of(null);
      })
    );
  }
}
